/**
 * NPJB-616 — recovery from stale lazy-route chunks after a deploy.
 *
 * Every route in `src/router.tsx` is code-split via `lazy: () => import(...)`.
 * A deployed container holds only its own build (`serve dist`, see Dockerfile),
 * so once a new image goes live the previous build's content-hashed chunks are
 * gone. A browser still running the old build asks for a file that no longer
 * exists, the static server answers with the SPA shell (or a 404 page), the
 * browser's strict MIME check rejects the module, and the rejected `import()`
 * lands on the router's `errorElement` — the 500 "Something went wrong" page.
 *
 * That chunk is never coming back, so the only real recovery is to re-fetch
 * `index.html` and pick up the new chunk graph. Reloads are capped within a
 * short window so a genuinely broken deploy surfaces the error page instead of
 * thrashing. The window ends, so the next deploy gets a fresh budget — a plain
 * per-tab counter ran out after two deploys and never came back.
 *
 * NPJB-838 — the window alone still failed three deploys inside five minutes.
 * So the budget also belongs to one build (`__APP_VERSION__`, the git SHA in
 * CI). A page from a new build starts from zero. A chunk that keeps failing
 * on the same build still stops at MAX_RELOADS.
 * Eliminating the root cause is tracked in NPJB-680.
 */

const RELOAD_KEY = 'dz:stale-chunk-reloads';

/** Hard ceiling on automatic reloads within one window — keeps a bad deploy from looping. */
const MAX_RELOADS = 2;

/**
 * How long a run of reloads counts against the cap. A broken deploy reloads at
 * most MAX_RELOADS times in this window. After it, the budget starts fresh.
 */
const RELOAD_WINDOW_MS = 5 * 60 * 1000;

/** Substrings browsers use when a dynamic import, or its preload, fails. */
const STALE_CHUNK_SIGNATURES = [
  'failed to fetch dynamically imported module', // Chrome / Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'failed to load module script', // Chrome — MIME-type rejection
  'expected a javascript-or-wasm module script', // Chrome — MIME-type rejection
  'is not a valid javascript mime type',
  'unable to preload css for', // Vite's CSS preload helper
];

type ChunkStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface StaleChunkDeps {
  /** Defaults to `window.sessionStorage`; `null` means storage is unavailable. */
  storage?: ChunkStorage | null;
  /** Defaults to a full page reload. */
  reload?: () => void;
  /** Defaults to `Date.now`. */
  now?: () => number;
  /** Defaults to `__APP_VERSION__`. The budget only counts reloads from this build. */
  build?: string;
}

/** What storage holds: `<count>@<ms of the first reload in this window>@<build>`. */
interface ReloadBudget {
  count: number;
  since: number;
}

function defaultStorage(): ChunkStorage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    // Reading sessionStorage itself throws when site data is blocked.
    return null;
  }
}

function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  // `?? ''`: an HttpError (src/services/http.ts) keeps `message` undefined on purpose.
  if (error instanceof Error) return error.message ?? '';
  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message: unknown };
    return typeof message === 'string' ? message : '';
  }
  return '';
}

/** True when `error` is a lazy-chunk load failure rather than an application error. */
export function isStaleChunkError(error: unknown): boolean {
  const message = messageOf(error).toLowerCase();
  if (!message) return false;
  return STALE_CHUNK_SIGNATURES.some((signature) => message.includes(signature));
}

/**
 * Without storage we cannot remember that a reload already happened, so the
 * budget reports as spent. Declining to reload leaves the user on the error
 * page — no worse than before the fix, and never an infinite loop.
 */
const SPENT: ReloadBudget = { count: MAX_RELOADS, since: Number.POSITIVE_INFINITY };
const FRESH: ReloadBudget = { count: 0, since: 0 };

function readBudget(storage: ChunkStorage | null, now: number, build: string): ReloadBudget {
  if (!storage) return SPENT;
  let raw: string | null;
  try {
    raw = storage.getItem(RELOAD_KEY);
  } catch {
    return SPENT;
  }
  const [countPart, sincePart, buildPart] = (raw ?? '').split('@');
  const count = Number(countPart);
  const since = Number(sincePart);
  // Corrupt, old-format (no time) or expired: start a fresh window.
  if (!Number.isInteger(count) || count <= 0 || !Number.isFinite(since)) return FRESH;
  // Reloads made by another build say nothing about this one (NPJB-838).
  if (buildPart !== build) return FRESH;
  if (now - since >= RELOAD_WINDOW_MS) return FRESH;
  return { count, since };
}

function writeBudget(storage: ChunkStorage | null, budget: ReloadBudget, build: string): boolean {
  if (!storage) return false;
  try {
    storage.setItem(RELOAD_KEY, `${budget.count}@${budget.since}@${build}`);
    return true;
  } catch {
    return false;
  }
}

function resolveDeps({
  storage = defaultStorage(),
  now = Date.now,
  build = __APP_VERSION__,
}: StaleChunkDeps) {
  return { storage, now: now(), build };
}

/** Whether a reload is still available — a pure read, safe to call during render. */
export function canRecoverFromStaleChunk(deps: StaleChunkDeps = {}): boolean {
  const { storage, now, build } = resolveDeps(deps);
  return readBudget(storage, now, build).count < MAX_RELOADS;
}

/** Consumes one reload from the budget and reloads. Returns false when exhausted. */
export function recoverFromStaleChunk(deps: StaleChunkDeps = {}): boolean {
  const { storage, now, build } = resolveDeps(deps);
  const budget = readBudget(storage, now, build);

  if (budget.count >= MAX_RELOADS) return false;
  const since = budget.count === 0 ? now : budget.since;
  if (!writeBudget(storage, { count: budget.count + 1, since }, build)) return false;

  const reload = deps.reload ?? (() => globalThis.location.reload());
  reload();
  return true;
}

/** Registers the Vite preload hook. Call once, from the app entrypoint. */
export function initStaleChunkRecovery(): void {
  globalThis.addEventListener('vite:preloadError', (event) => {
    // Only swallow Vite's throw when we are actually reloading. Otherwise let it
    // reach the router's errorElement, rather than resolving the import with a
    // broken module and failing in stranger ways downstream.
    if (recoverFromStaleChunk()) event.preventDefault();
  });
}
